import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldAlert,
  CheckCircle2,
  Loader2,
  Clock,
  Send,
  ThumbsUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const STATE_META = {
  none: { idx: 0, label: "Considering" },
  pre_adverse_sent: { idx: 1, label: "Pre-adverse sent · waiting" },
  final_adverse_sent: { idx: 2, label: "Final adverse sent" },
  proceeded: { idx: 2, label: "Proceeded with hire" },
};

function StepDot({ active, done, label }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`grid h-6 w-6 place-items-center rounded-full text-[10px] font-bold ${
          done
            ? "bg-emerald-600 text-white"
            : active
              ? "bg-amber-500 text-white"
              : "border border-slate-300 bg-white text-slate-400"
        }`}
      >
        {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : "·"}
      </div>
      <span
        className={`text-[11px] font-semibold uppercase tracking-wider ${
          done
            ? "text-emerald-700"
            : active
              ? "text-amber-700"
              : "text-slate-400"
        }`}
      >
        {label}
      </span>
    </div>
  );
}

export default function AdverseActionCard({ bgCheck, onChanged }) {
  const [templates, setTemplates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(null); // "pre_adverse" | "final_adverse"
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!bgCheck?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/background-checks/${bgCheck.id}/adverse-templates`
        );
        if (!cancelled) setTemplates(data);
      } catch {
        // best-effort
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bgCheck?.id]);

  const state = bgCheck.adverse_action_state || "none";
  const meta = STATE_META[state] || STATE_META.none;

  const openEditor = (step) => {
    if (!templates) return;
    setDraft(
      step === "pre_adverse"
        ? templates.pre_adverse_html
        : templates.final_adverse_html
    );
    setEditorOpen(step);
  };

  const submit = async () => {
    if (!editorOpen) return;
    setSubmitting(true);
    try {
      await api.post(`/background-checks/${bgCheck.id}/adverse-action`, {
        step: editorOpen,
        message: draft,
      });
      toast.success(
        editorOpen === "pre_adverse"
          ? "Pre-adverse notice sent — 7-day clock started"
          : "Final adverse notice sent"
      );
      setEditorOpen(null);
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not send notice"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const proceed = async () => {
    if (
      !window.confirm(
        "Clear the adverse path and proceed with hiring this candidate?"
      )
    )
      return;
    try {
      await api.post(`/background-checks/${bgCheck.id}/adverse-action`, {
        step: "proceed",
      });
      toast.success("Marked as proceeded — adverse action workflow closed");
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  // Compute days remaining in waiting period (snapshot from props at mount)
  const computeDaysRemaining = (bg, st) => {
    if (st !== "pre_adverse_sent" || !bg?.pre_adverse_sent_at) return null;
    const wait = bg.waiting_period_days || 7;
    const sentAt = new Date(bg.pre_adverse_sent_at);
    const elapsed = (Date.now() - sentAt.getTime()) / 86400000;
    return Math.max(0, Math.ceil(wait - elapsed));
  };
  const [daysRemaining] = useState(() => computeDaysRemaining(bgCheck, state));

  return (
    <section
      data-testid="adverse-action-card"
      className="mt-6 border border-amber-300 bg-amber-50"
    >
      <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-100 px-5 py-3">
        <ShieldAlert className="h-4 w-4 text-amber-700" />
        <div className="flex-1">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-900">
            Adverse Action workflow · FCRA
          </div>
          <div className="text-[11px] text-amber-800">
            Background check returned <strong>Consider</strong>. Follow the
            steps below to stay FCRA-compliant.
          </div>
        </div>
        <span className="border border-amber-300 bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800">
          {meta.label}
        </span>
      </div>

      <div className="space-y-4 p-5">
        {/* Step indicators */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <StepDot active={state === "none"} done={meta.idx >= 1} label="1. Review report" />
          <StepDot
            active={state === "none"}
            done={meta.idx >= 1}
            label="2. Send pre-adverse"
          />
          <StepDot
            active={state === "pre_adverse_sent"}
            done={meta.idx >= 2}
            label="3. Wait 7 days"
          />
          <StepDot
            active={state === "pre_adverse_sent"}
            done={state === "final_adverse_sent" || state === "proceeded"}
            label="4. Final decision"
          />
        </div>

        {/* Action buttons by state */}
        {loading ? (
          <div className="flex h-16 items-center justify-center text-amber-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : state === "none" ? (
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="adverse-pre-adverse-btn"
              onClick={() => openEditor("pre_adverse")}
              className="bg-amber-600 text-white hover:bg-amber-500"
            >
              <Send className="mr-2 h-4 w-4" />
              Send pre-adverse notice
            </Button>
            <Button
              data-testid="adverse-proceed-btn"
              variant="outline"
              onClick={proceed}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              <ThumbsUp className="mr-2 h-4 w-4" />
              Proceed with hire anyway
            </Button>
          </div>
        ) : state === "pre_adverse_sent" ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 border border-amber-200 bg-white px-3 py-2 text-sm text-amber-900">
              <Clock className="h-4 w-4 text-amber-600" />
              {daysRemaining > 0 ? (
                <span>
                  Pre-adverse notice sent.{" "}
                  <strong>{daysRemaining}</strong> day
                  {daysRemaining === 1 ? "" : "s"} remaining in waiting period.
                  Candidate may respond directly via Checkr.
                </span>
              ) : (
                <span>
                  Waiting period elapsed. You can now send the final notice or
                  proceed with hire.
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                data-testid="adverse-final-btn"
                onClick={() => openEditor("final_adverse")}
                disabled={daysRemaining > 0}
                className="bg-rose-600 text-white hover:bg-rose-500 disabled:bg-slate-300"
              >
                <Send className="mr-2 h-4 w-4" />
                Send final adverse notice
              </Button>
              <Button
                data-testid="adverse-proceed-btn"
                variant="outline"
                onClick={proceed}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              >
                <ThumbsUp className="mr-2 h-4 w-4" />
                Reconsider — proceed with hire
              </Button>
            </div>
          </div>
        ) : (
          <div
            data-testid="adverse-closed-state"
            className="flex items-center gap-2 border border-emerald-200 bg-white px-3 py-2 text-sm text-emerald-900"
          >
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            Workflow closed: <strong>{meta.label}</strong>.
          </div>
        )}
      </div>

      <Dialog
        open={!!editorOpen}
        onOpenChange={(o) => !o && setEditorOpen(null)}
      >
        <DialogContent className="max-w-2xl" data-testid="adverse-editor-modal">
          <DialogHeader>
            <DialogTitle className="text-base">
              {editorOpen === "pre_adverse"
                ? "Pre-adverse action notice"
                : "Final adverse action notice"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-[11px] text-slate-500">
              Edit the email body below before sending. FCRA-required language
              about consumer reporting agency, dispute rights, and that the
              employer (not Checkr) made the decision is included.
            </p>
            <textarea
              data-testid="adverse-editor-textarea"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={14}
              className="w-full border border-slate-300 p-2 font-mono text-xs outline-none focus:border-amber-500"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setEditorOpen(null)}
                data-testid="adverse-editor-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || !draft.trim()}
                data-testid="adverse-editor-send"
                className={
                  editorOpen === "final_adverse"
                    ? "bg-rose-600 text-white hover:bg-rose-500"
                    : "bg-amber-600 text-white hover:bg-amber-500"
                }
              >
                {submitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                Send notice
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
